import * as React from "react";
import { Mutation } from "react-apollo";
import gql from "graphql-tag";
import Select from "react-select";
import { Row, Col, Form, Label, FormGroup, Input } from "reactstrap";

/** GraphQL */
import { updateCompany } from "../../graphql/mutations";

import {
  UpdateCompanyMutation,
  UpdateCompanyMutationVariables,
  UserRole,
  CreateCompanyInput,
  Province,
  Industry
} from "../../API";

/** Presentation/UI */
import ErrorMessage from "../../Components/Styled/ErrorMessage";
import StyledButton from "../../Components/Styled/Button";
import Link from "../../Components/Styled/Link";

/** Custom Types */
import { Error } from "../../CustomTypes";

import { Colors } from "src/Themes";
import styled from "styled-components";
import { TableHeaderContainer } from "src/Components/Styled/ListViewElements";
import Loader from "src/Components/Loader";
import { formatDropdown, getUserRole } from "src/Utils/Helpers";

type SelectProvinceType = {
  value: Province;
  label: string;
};

type selectIndustryType = {
  value: Industry;
  label: string;
};

type Props = {
  roles?: UserRole[];
  companyData?: CreateCompanyInput | null;
  editCompany?: boolean;
  closeModal(): void;
  notification(message: string): void;
};

type State = {
  loading: boolean;
  error: Error;
  readOnly: boolean;
  archived: boolean;
  id: string;
  name: string;
  emailAddress: string;
  contactNumber: string;
  address: string;
  // physicalAddress: string;
  province?: Province | null | undefined;
  industry?: Industry | null | undefined;
  numberOfEmployees?: string;
  editCompany: boolean;
};

const ToggleButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

class CompanyView extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { companyData } = props;
    this.state = {
      loading: false,
      error: null,
      readOnly: this.props.editCompany ? false : true,
      name: companyData && companyData.name ? companyData.name : "",
      id: companyData && companyData.id ? companyData.id : "",
      emailAddress:
        companyData && companyData.emailAddress ? companyData.emailAddress : "",
      contactNumber:
        companyData && companyData.contactNumber
          ? companyData.contactNumber
          : "",
      address: companyData && companyData.address ? companyData.address : "",
      // physicalAddress:
      //   companyData && companyData.physicalAddress
      //     ? companyData.physicalAddress
      //     : "",
      province:
        companyData && companyData.province ? companyData.province : null,
      numberOfEmployees:
        companyData && companyData.numberOfEmployees
          ? companyData.numberOfEmployees
          : "",
      industry:
        companyData && companyData.industry ? companyData.industry : null,
      archived: false,
      editCompany: this.props.editCompany || false
    };
  }

  /** Validation */
  validateForm = (): boolean => {
    return true;
  };

  /** Error */
  setError = (error: string): void => {
    this.setState(
      {
        error
      },
      () => {
        setTimeout(() => {
          this.setState({ error: null });
        }, 3000);
      }
    );
  };

  /** Edit a company */
  editCompany = async (updateCompanyMutation: ({}) => Promise<any>) => {
    const { closeModal, notification } = this.props;
    const {
      name,
      id,
      emailAddress,
      contactNumber,
      address,
      // physicalAddress,
      province,
      numberOfEmployees,
      industry
    } = this.state;
    this.setState({ loading: true });
    // update the user in dynamodb
    updateCompanyMutation({
      variables: {
        input: {
          id,
          name,
          emailAddress,
          contactNumber,
          address,
          // physicalAddress,
          province,
          numberOfEmployees,
          industry
        }
      }
    })
      .then(() => {
        this.setState({ loading: false });
        closeModal();
        notification(`Updated ${name} details`);
      })
      .catch(err => {
        this.setState({ loading: false });
        this.setError(err.message);
      });
  };

  toggleEdit = () => {
    this.setState({ readOnly: !this.state.readOnly });
  };

  toggleArchived = () => {
    this.setState({ archived: !this.state.archived });
  };

  // Select company province
  selectProvince = (province: SelectProvinceType): void => {
    this.setState({ province: province.value });
  };

  // format constants into dropdowns for provinces
  listOfProvinces = (consts: object) => {
    return Object.keys(consts).map((item: string) => {
      return formatDropdown(item);
    });
  };

  // select company industry
  selectIndustry = (industry: selectIndustryType): void => {
    this.setState({ industry: industry.value });
  };

  // format constants into dropdowns for insdustries
  listOfIndustries = (consts: object) => {
    return Object.keys(consts).map((item: string) => {
      return formatDropdown(item);
    });
  };

  returnFormFields = () => {
    const {
      id,
      name,
      emailAddress,
      contactNumber,
      address,
      // physicalAddress,
      province,
      numberOfEmployees,
      industry,
      error,
      readOnly,
      archived
    } = this.state;

    return (
      <React.Fragment>
        <ToggleButtonsContainer>
          <FormGroup>
            {readOnly ? (
              <Row>
                <Col xs={4} md={4} lg={4}>
                  <StyledButton
                    type="button"
                    label="Edit"
                    width="100px"
                    onClick={() => this.toggleEdit()}
                    color={Colors.coal}
                    background={Colors.background}
                  />
                </Col>
                {!this.state.editCompany ? (
                  <Col xs={4} md={4} lg={4}>
                    <Link
                      to={{
                        pathname: "/companies/team",
                        params: {
                          containerTitle: name + " Team members",
                          companyData: this.props.companyData
                        }
                      }}
                      label={
                        <StyledButton
                          type="button"
                          label="View Team"
                          width="100px"
                          color={Colors.coal}
                          background={Colors.background}
                        />
                      }
                    />
                  </Col>
                ) : null}
              </Row>
            ) : (
              <FormGroup>
                <Row>
                  <Col xs={4} md={4} lg={4}>
                    <StyledButton
                      type="submit"
                      label={!this.state.loading ? "Save" : <Loader />}
                      width="100px"
                      color={Colors.coal}
                      background={Colors.background}
                    />
                  </Col>
                  <Col xs={4} md={4} lg={4}>
                    <StyledButton
                      type="button"
                      label="Cancel"
                      width="100px"
                      onClick={() =>
                        this.state.editCompany
                          ? this.props.closeModal()
                          : this.toggleEdit()
                      }
                      color={Colors.coal}
                      background={Colors.background}
                    />
                  </Col>
                  {!this.state.editCompany ? (
                    <Col xs={4} md={4} lg={4}>
                      <Link
                        to={{
                          pathname: "/companies/team",
                          params: {
                            containerTitle: name + " Team members",
                            companyData: this.props.companyData
                          }
                        }}
                        label={
                          <StyledButton
                            type="button"
                            label="View Team"
                            width="100px"
                            color={Colors.coal}
                            background={Colors.background}
                          />
                        }
                      />
                    </Col>
                  ) : null}
                </Row>
              </FormGroup>
            )}
          </FormGroup>
        </ToggleButtonsContainer>
        <Row>
          <Col xs={12} md={12} lg={12}>
            <FormGroup>
              <Label for="name">Name</Label>
              <Input
                type="text"
                name="name"
                value={name}
                id="name"
                placeholder="Name"
                onChange={e => this.setState({ name: e.target.value })}
                disabled={readOnly}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={12} lg={12}>
            <FormGroup>
              <Label for="emailAddress">Email Address</Label>
              <Input
                type="email"
                name="emailAddress"
                value={emailAddress}
                id="emailAddress"
                placeholder="Email"
                onChange={e => this.setState({ emailAddress: e.target.value })}
                disabled={readOnly}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={12} lg={12}>
            <FormGroup>
              <Label for="contactNumber">Contact Number</Label>
              <Input
                type="tel"
                name="contactNumber"
                value={contactNumber}
                id="contactNumber"
                placeholder="contactNumber"
                onChange={e => this.setState({ contactNumber: e.target.value })}
                disabled={readOnly}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={12} lg={12}>
            <FormGroup>
              <Label for="numberOfEmployees">Number of Employees</Label>
              <Input
                type="tel"
                name="numberOfEmployees"
                value={numberOfEmployees}
                id="numberOfEmployees"
                placeholder="numberOfEmployees"
                onChange={e =>
                  this.setState({ numberOfEmployees: e.target.value })
                }
                disabled={readOnly}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={12} lg={12}>
            <FormGroup>
              <Label>Industry</Label>
              <Select
                options={this.listOfIndustries(Industry)}
                onChange={this.selectIndustry}
                value={formatDropdown(industry)}
                placeholder=""
                className="select-styling"
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={12} lg={12}>
            <FormGroup>
              <Label for="address">Address</Label>
              <Input
                type="text"
                name="address"
                value={address}
                id="address"
                placeholder="Address"
                onChange={e => this.setState({ address: e.target.value })}
                disabled={readOnly}
              />
            </FormGroup>
          </Col>
        </Row>
        {/* <Row>
          <Col xs={12} md={12} lg={12}>
            <FormGroup>
              <Label for="physicalAddress">Address Line 2</Label>
              <Input
                type="text"
                name="physicalAddress"
                value={physicalAddress}
                id="physicalAddress"
                placeholder="Address Line 2"
                onChange={e =>
                  this.setState({ physicalAddress: e.target.value })
                }
                disabled={readOnly}
              />
            </FormGroup>
          </Col>
        </Row> */}
        <Row>
          <Col xs={12} md={12} lg={12}>
            <FormGroup>
              <Label>Province</Label>
              <Select
                options={this.listOfProvinces(Province)}
                onChange={this.selectProvince}
                value={formatDropdown(province)}
                placeholder=""
                className="select-styling"
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={6} md={6} lg={6}>
            <ToggleButtonsContainer>
              {id !== "" && getUserRole() !== "CONSUMER_ADMIN" ? (
                <React.Fragment>
                  <Label>{archived ? "Unarchive" : "Archive"} company?</Label>
                  <Row>
                    <Col xs={6} md={6} lg={6}>
                      <FormGroup>
                        <TableHeaderContainer>
                          <StyledButton
                            type="button"
                            label="Yes"
                            width="100px"
                            onClick={() => this.toggleArchived()}
                            color={archived ? Colors.snow : Colors.coal}
                            background={
                              archived ? Colors.coralPink : Colors.background
                            }
                          />
                        </TableHeaderContainer>
                      </FormGroup>
                    </Col>
                  </Row>
                </React.Fragment>
              ) : null}
            </ToggleButtonsContainer>
          </Col>
        </Row>
        <br />
        {error && <ErrorMessage errorMessage={error} />}
      </React.Fragment>
    );
  };

  render() {
    return (
      <Mutation<UpdateCompanyMutation, UpdateCompanyMutationVariables>
        mutation={gql(updateCompany)}
      >
        {updateCompanyMutation => (
          <Form
            onSubmit={e => {
              e.preventDefault();
              if (this.validateForm()) {
                this.editCompany(updateCompanyMutation);
              }
            }}
          >
            {this.returnFormFields()}
          </Form>
        )}
      </Mutation>
    );
  }
}

export default CompanyView;



// WEBPACK FOOTER //
// ./src/Containers/Companies/CompanyView.tsx